/**
 * VoiceBiomarkersCard — iter631 — Гласови биомаркери MVP.
 * 30-сек запис чрез MediaRecorder → POST /voice-biomarkers/sample → акустичен анализ.
 * Корелира със Стрес и Психо-емоционален близнак.
 */
import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Mic, Square, Loader2, Sparkles, History } from "lucide-react";
import { toast } from "sonner";

const MAX_SECONDS = 30;

export default function VoiceBiomarkersCard() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [recording, setRecording] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/voice-biomarkers/history?days=30`);
      setHistory(data);
    } catch { /* */ }
  };
  useEffect(() => { loadHistory(); /* eslint-disable-next-line */ }, [API]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/webm")
        ? "audio/webm"
        : "audio/mp4";
      const mr = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime });
        await uploadSample(blob, mime);
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
      setElapsed(0);
      setResult(null);
      timerRef.current = setInterval(() => {
        setElapsed((p) => {
          if (p + 1 >= MAX_SECONDS) {
            stopRecording();
            return MAX_SECONDS;
          }
          return p + 1;
        });
      }, 1000);
    } catch (e) {
      toast.error("Нямам достъп до микрофона. Дай разрешение в настройките.");
    }
  };

  const stopRecording = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    } catch {}
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setRecording(false);
  };

  const uploadSample = async (blob, mime) => {
    setAnalyzing(true);
    try {
      const form = new FormData();
      const ext = mime.includes("mp4") ? "m4a" : "webm";
      form.append("audio", blob, `voice-${Date.now()}.${ext}`);
      const { data } = await axios.post(`${API}/voice-biomarkers/sample`, form, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 30000,
      });
      setResult(data);
      toast.success(`Гласов скор: ${data.score ?? "—"}/100`);
      loadHistory();
    } catch (e) {
      toast.error("Грешка при анализа на записа.");
    } finally {
      setAnalyzing(false);
    }
  };

  const scoreColor = (s) => s == null ? "text-slate-400"
    : s >= 80 ? "text-emerald-500"
    : s >= 60 ? "text-amber-500"
    : "text-rose-500";

  return (
    <div className={`p-4 rounded-2xl border-2 ${isDark ? "bg-purple-900/20 border-purple-700/30" : "bg-purple-50 border-purple-200"}`} data-testid="voice-biomarkers-card">
      <div className="flex items-center gap-2 mb-2">
        <Mic className="w-4 h-4 text-purple-500" />
        <h3 className="text-sm font-bold">🎙️ Гласови биомаркери</h3>
        <span className="ml-auto text-[10px] opacity-60">Ранна проверка</span>
      </div>
      <p className="text-[11px] opacity-80 mb-3">
        30 сек запис на свободна реч. Анализираме pitch стабилност, jitter, енергия и паузи —
        акустични индикатори за стрес и емоционално състояние.
      </p>

      {!recording && !analyzing && (
        <Button
          onClick={startRecording}
          className="bg-purple-600 hover:bg-purple-700 text-white w-full"
          data-testid="voice-start-btn"
        >
          <Mic className="w-4 h-4 mr-2" /> Започни 30-сек запис
        </Button>
      )}

      {recording && (
        <div className="space-y-2" data-testid="voice-recording-state">
          <div className={`text-center text-3xl font-bold ${isDark ? "text-purple-200" : "text-purple-800"}`} data-testid="voice-timer">
            🔴 {elapsed}s / {MAX_SECONDS}s
          </div>
          <div className={`h-1.5 rounded-full overflow-hidden ${isDark ? "bg-slate-800" : "bg-purple-100"}`}>
            <div className="h-full bg-purple-500 transition-all" style={{ width: `${(elapsed / MAX_SECONDS) * 100}%` }} />
          </div>
          <p className="text-[10px] opacity-70 text-center">Говори свободно — разкажи как мина денят, какво ядеш сега, или прочети нещо.</p>
          <Button onClick={stopRecording} variant="outline" className="w-full" data-testid="voice-stop-btn">
            <Square className="w-4 h-4 mr-2" /> Спри сега
          </Button>
        </div>
      )}

      {analyzing && (
        <div className="flex items-center justify-center py-4 gap-2 text-sm" data-testid="voice-analyzing">
          <Loader2 className="w-4 h-4 animate-spin text-purple-500" /> Анализ на акустичните характеристики…
        </div>
      )}

      {result && !recording && !analyzing && (
        <div className="space-y-2 mt-3" data-testid="voice-result">
          <div className="flex items-center justify-between">
            <span className="text-[10px] uppercase font-bold opacity-70">Резултат</span>
            <span className={`text-3xl font-bold ${scoreColor(result.score)}`} data-testid="voice-result-score">
              {result.score ?? "—"}/100
            </span>
          </div>
          <p className="text-[11px] leading-relaxed" data-testid="voice-result-explanation">{result.explanation_bg}</p>
          {result.notes_bg && result.notes_bg.length > 0 && (
            <ul className="text-[10px] opacity-80 space-y-0.5 pl-3 list-disc">
              {result.notes_bg.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          )}
          <details className="text-[10px] opacity-60">
            <summary className="cursor-pointer">Акустични характеристики</summary>
            <div className="mt-1 grid grid-cols-2 gap-1 font-mono">
              {Object.entries(result.features || {}).slice(0, 8).map(([k, v]) => (
                <div key={k}>{k}: {String(v ?? "—")}</div>
              ))}
            </div>
          </details>
        </div>
      )}

      {history && history.count > 0 && (
        <div className={`mt-3 pt-3 border-t ${isDark ? "border-purple-700/30" : "border-purple-200"}`} data-testid="voice-history">
          <div className="flex items-center justify-between text-[10px]">
            <span className="flex items-center gap-1 opacity-70">
              <History className="w-3 h-3" /> История · {history.count} записа (30д)
            </span>
            {history.avg_score != null && (
              <span className={`font-bold ${scoreColor(history.avg_score)}`} data-testid="voice-avg-score">
                Avg {history.avg_score}/100
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
